import React, { useMemo, useState } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import {
	DataGridPro,
	useGridApiRef,
	GridRowId,
	GridSortModel,
	GridRowParams,
	GridCellParams,
	MuiEvent,
} from '@mui/x-data-grid-pro';
import { InventoryFields, SkuItem } from 'common/modules/inventory';
import { useSelector } from 'react-redux';
import * as StockSelectors from 'selectors/StockSelectors';
import { getFilteredRowsBySearchValue } from '../../utils';
import { useTranslation } from 'services/localization';
import TableToolbar from '../../components/TableToolbar';
import CustomNoRowsOverlay from '../../components/CustomNoRowsOverlay';
import CustomColumnsPanel from '../../components/CustomColumnsPanel';
import { RiLayoutColumnLine, RiFilter3Fill, RiDownloadLine } from 'react-icons/ri';
import { api } from 'common/frontend/api';
import { getFixedColumns, getActionsColumn, SkuColumnFieldsWithTypes } from './columns';
import { getSkuRows, rowToSkuItem } from './rows';
import { getDataGridLocales } from 'services/localization/getDataGridLocales';
import { isEmpty } from 'lodash';

interface Props {
	inventoryFields: InventoryFields;
	skuItems: SkuItem[];
	selectedRowIds: string[];
	disabledRowIds: string[];
	setSelectedRowIds: (ids: string[]) => void;
	searchValue: string;
	selectedLocationIds?: string[];
}

const SkuTable = (props: Props) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const stockProducts = useSelector(StockSelectors.stockProductsData);
	const { skuItems, searchValue, disabledRowIds, selectedLocationIds } = props;
	const apiRef = useGridApiRef();
	const [activeRowId, setActiveRowId] = useState<string | null>(null);
	const [pageSize, setPageSize] = useState<number>(50);
	const [sortModel, setSortModel] = useState<GridSortModel>([
		{
			field: 'sku_code',
			sort: 'asc',
		},
	]);
	const hasFilters = !!searchValue || !isEmpty(skuItems);

	const skuRowItems = useMemo(
		() =>
			getSkuRows({
				skuItems,
				stockProducts,
				selectedLocationIds,
				t,
			}),
		[selectedLocationIds, skuItems, stockProducts, t],
	);

	const fixedColumns = useMemo(() => getFixedColumns(t), [t]);
	const actionsColumn = useMemo(() => {
		const handleEditClick = (id: GridRowId) => (
			event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		) => {
			event.stopPropagation();
			setActiveRowId(id.toString());
			apiRef.current.setRowMode(id, 'edit');
		};

		const handleSaveClick = (id: GridRowId) => async (
			event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		) => {
			event.stopPropagation();
			apiRef.current.commitRowChange(id);
			apiRef.current.setRowMode(id, 'view');

			const row = apiRef.current.getRow(id) as SkuColumnFieldsWithTypes;
			const hasZeroQuantity = row.quantity === 0;
			const { skuCode, skuName, itemType } = rowToSkuItem(row);
			await api()
				.skuItems.doc(id.toString())
				.update({
					skuCode,
					skuName,
					...(hasZeroQuantity && { itemType }),
				});
			setActiveRowId(null);
		};

		const handleCancelClick = (id: GridRowId) => (
			event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		) => {
			event.stopPropagation();
			apiRef.current.setRowMode(id, 'view');
			setActiveRowId(null);
		};

		return getActionsColumn({
			apiRef,
			activeRowId,
			disabledRowIds: disabledRowIds,
			actions: {
				onSave: handleSaveClick,
				onCancel: handleCancelClick,
				onEdit: handleEditClick,
			},
			t,
		});
		// We do not want to include "disabledRowIds" as it's an array and would unnecessarily cause re-render of whole Table on each render
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeRowId, apiRef, t]);

	const columns = useMemo(() => [...fixedColumns, ...actionsColumn], [actionsColumn, fixedColumns]);
	const filteredRows = useMemo(() => getFilteredRowsBySearchValue(skuRowItems, searchValue), [
		searchValue,
		skuRowItems,
	]);

	const disableMuiDefaultEvent = (params: GridRowParams | GridCellParams, event: MuiEvent) => {
		event.defaultMuiPrevented = true;
	};

	return (
		<div className={classes.paper}>
			<div className={classes.flexGrow}>
				<DataGridPro
					autoHeight
					apiRef={apiRef}
					checkboxSelection
					className={classes.datagridRoot}
					getRowClassName={(params) =>
						!!activeRowId && activeRowId !== params.id.toString() ? 'disabled-row' : ''
					}
					density="standard"
					rows={filteredRows}
					columns={columns}
					localeText={getDataGridLocales(t)}
					disableColumnReorder={true}
					disableSelectionOnClick
					editMode="row"
					sortModel={sortModel}
					pagination
					pageSize={pageSize}
					onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
					isRowSelectable={(params: GridRowParams) => !disabledRowIds.includes(params.row.id)}
					rowsPerPageOptions={[25, 50, 100]}
					onSortModelChange={(model) => setSortModel(model)}
					selectionModel={props.selectedRowIds}
					onSelectionModelChange={(newSelectionModel) => {
						props.setSelectedRowIds(newSelectionModel.map((id) => id.toString()));
					}}
					components={{
						Toolbar: TableToolbar,
						ColumnSelectorIcon: RiLayoutColumnLine,
						OpenFilterButtonIcon: RiFilter3Fill,
						ExportIcon: RiDownloadLine,
						NoRowsOverlay: CustomNoRowsOverlay,
						ColumnsPanel: CustomColumnsPanel,
					}}
					componentsProps={{
						noRowsOverlay: {
							title: !!searchValue
								? t('inventory.sku.table.noSearchResults', {
										searchValue,
										defaultValue: 'No matching SKUs found for "{{searchValue}}"',
								  })
								: hasFilters
								? t('inventory.sku.table.noMatchingResults', 'No matching SKUs found')
								: t('inventory.sku.table.emptyTitle', 'You have no stock keeping units'),
							body: !!hasFilters
								? ''
								: t(
										'inventory.sku.table.emptyDescription',
										`Once you've added SKUs, you'll be able to manage them here.`,
								  ),
						},
						columnsPanel: {
							dataGridName: 'inventory_sku',
						},
					}}
					onRowEditStart={disableMuiDefaultEvent}
					onRowEditStop={disableMuiDefaultEvent}
					onCellFocusOut={disableMuiDefaultEvent}
				/>
			</div>
		</div>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		paper: {
			display: 'flex',
			flexDirection: 'column',
			flex: 1,
			backgroundColor: theme.palette.background.paper,
			border: `1px solid ${theme.palette.border.paper}`,
			borderRadius: 4,
		},
		flexGrow: {
			display: 'flex',
			flexGrow: 1,
		},
		datagridRoot: {
			backgroundColor: theme.palette.background.paper,
			'& .MuiDataGrid-columnHeaders': {
				backgroundColor: theme.palette.background.secondary,
			},
			'& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
				outline: 'none',
			},
			'& .MuiDataGrid-menuIcon': {
				visibility: 'visible',
				width: 'auto',
			},
			'& .MuiDataGrid-toolbar': {
				padding: theme.spacing(1),
			},
			'& .MuiDataGrid-columnSeparator': {
				color: theme.palette.border.table,
			},
			'& .MuiDataGrid-cell': {
				border: 'none',
			},
			'& .disabled-row': {
				color: theme.palette.text.disabled,
				outline: 'none',
				'& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
					outline: 'none',
				},
			},
			'& .MuiDataGrid-row': {
				borderBottom: `1px solid ${theme.palette.border.table}`,
			},
			'& .MuiDataGrid-row--editable': {
				boxShadow: 'none',
				'& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
					outline: 'none',
				},
			},
			'& .MuiDataGrid-row--editing .MuiDataGrid-cell': {
				backgroundColor: 'transparent', //To show row border
			},
			'& .MuiDataGrid-cell--editing.MuiDataGrid-cell': {
				boxShadow: 'none',
				outlineOffset: 'initial',
				padding: '0 10px',
				alignItems: 'center',
			},
			'& .hide-column-separator': {
				'& .MuiDataGrid-columnSeparator': {
					opacity: '0 !important',
				},
			},
			'& .MuiDataGrid-columnHeaders + div': {
				//Forces scrollbar to table bottom
				minHeight: '100%',
			},
			'& .MuiDataGrid-virtualScroller': {
				//Forces scrollbar to table bottom
				minHeight: 'calc(100% - 56px) !important',
			},
		},
	}),
);

export default SkuTable;
